/**
 * DOCTRACK part 3 (design 4.6; his D13) - the words for a Property Database record's numbers, on the deal page and the
 * record page: what a save did on the record, where each number came from, and the record's number beside the deal's.
 * Pure. Dates are his Pacific days.
 */
import type { DealNumberField, DealRecordOutcome, FieldValuesView, RankedField, RecordAskView, RecordDocKind, RecordNumber, ValueShown } from '../types';
import { pacificToday } from '../utils/deadlineDates';

/** Each ranked source in his words. */
export const SOURCE_WORDS: Readonly<Record<string, string>> = {
  pdf_extract_title: 'title report', pdf_extract: 'OM or property profile', costar: 'CoStar', crexi: 'Crexi', attom: 'ATTOM',
  capitalize: 'Capitalize', county_assessor: 'county assessor', county_recorder: 'county recorder', city_gis: 'city GIS',
  title: 'title company', reonomy: 'Reonomy', loopnet: 'LoopNet', mls: 'MLS', public_records: 'public records',
  executed_instrument: 'signed contract', derived_from_city: 'derived from the city', other: 'an import',
};
/** The document a number came from, when one did. */
export const KIND_WORDS: Readonly<Record<RecordDocKind, string>> = {
  title_report: 'title report', offering_memorandum: 'OM', property_profile: 'property profile',
};

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const DAY = /^\d{4}-\d{2}-\d{2}$/;
const number = (v: unknown): number | null => {
  if (v == null || v === '') return null;
  const n = typeof v === 'number' ? v : Number(String(v).replace(/[$,\s]/g, ''));
  return Number.isFinite(n) ? n : null;
};
const grouped = (n: number) => n.toLocaleString('en-US', { maximumFractionDigits: 2 });

/** A day (YYYY-MM-DD) as itself; a moment as its Pacific day; anything else as none. */
const dayOf = (date: string): string | null => {
  if (DAY.test(date)) return date;
  const ms = Date.parse(date);
  return Number.isFinite(ms) ? pacificToday(ms) : null;
};

/** One field's value in its unit: "8,000 SF", "0.96 acres", "$570,000", "Feb 2, 2012"; the rest as stored. */
export function formatFieldValue(field: RankedField, value: string): string {
  const n = number(value);
  if ((field === 'building_size' || field === 'land_area_sf') && n != null) return `${grouped(n)} SF`;
  if (field === 'lot_size_acres' && n != null) return `${n} acres`;
  if (field === 'last_sale_price' && n != null) return `$${grouped(n)}`;
  if (field === 'last_sale_date' && DAY.test(value)) {
    const [y, m, d] = value.split('-').map(Number);
    return `${MONTHS[m - 1]} ${d}, ${y}`;
  }
  return value;
}

/** "Oct 20" in this Pacific year, "Mar 2024" in another, '' for none. */
export function whenLabel(date: string | null | undefined, now: Date): string {
  if (!date) return '';
  const day = dayOf(date);
  if (!day) return '';
  const [y, m, d] = day.split('-').map(Number);
  return y === Number(pacificToday(now.getTime()).slice(0, 4)) ? `${MONTHS[m - 1]} ${d}` : `${MONTHS[m - 1]} ${y}`;
}

const firstName = (name: string | null): string | null => (name && name.trim() !== '' ? name.trim().split(/\s+/)[0] : null);
const sourceWords = (v: ValueShown): string => (v.docKind ? KIND_WORDS[v.docKind] : v.source ? SOURCE_WORDS[v.source] ?? v.source : '');
const joined = (...parts: string[]) => parts.filter((p) => p !== '').join(', ');

/**
 * Where a number came from: his ("Joseph, on the Grocery Outlet deal, Oct 20 - "owner said""), his pick ("CoStar, Jun
 * 2025 - picked by Joseph, Nov 4"), a source ("title report, Nov 1"), or "source not recorded".
 */
export function whoLabel(v: ValueShown, now: Date): string {
  const when = whenLabel(v.date, now);
  if (v.source === 'manual') {
    const who = joined(firstName(v.setByName) ?? 'Someone', v.setOn ? `on the ${v.setOn} deal` : '', when);
    return v.note ? `${who} - "${v.note}"` : who;
  }
  if (v.pickedAt || v.pickedByName) {
    const from = joined(sourceWords(v) || 'source not recorded', when);
    return `${from} - picked by ${joined(firstName(v.pickedByName) ?? 'someone', whenLabel(v.pickedAt, now))}`;
  }
  if (!v.source) return 'source not recorded';
  return joined(sourceWords(v), when);
}

/** What a deal-page save did on the building's record, in his words; null when the save touched none of the three. */
export function dealRecordNote(o: DealRecordOutcome | null): string | null {
  if (!o) return null;
  if (o.state === 'recorded') {
    return o.changed.length > 0
      ? 'Also saved on the Property Database record'
      : 'Kept on this deal only - clearing a number here never clears the Property Database record';
  }
  if (o.state === 'not_linked') return 'Kept on this deal only - not linked to a Property Database record';
  if (o.state === 'two_records') return 'Kept on this deal only - it points at two Property Database records';
  return 'Saved on this deal - the Property Database record could not be updated';
}

/** The save's body with his note, only when he wrote one. */
export function withRecordNote(updates: Record<string, unknown>, note: string): Record<string, unknown> {
  const t = (note ?? '').trim();
  return t === '' ? updates : { ...updates, record_note: t };
}

/**
 * The record's number beside the deal's - "Property Database record: 5,000 SF (title report, Nov 1)" - only when the
 * record holds one and it differs (the deal's lot size is compared with the record's land area).
 */
export function recordNumberBeside(field: DealNumberField, dealValue: unknown, numbers: RecordNumber[], now: Date): string | null {
  const recordField = field === 'lot_size' ? 'land_area_sf' : field;
  const shown = numbers.find((n) => n.field === recordField)?.inUse;
  if (!shown) return null;
  const mine = number(dealValue);
  const theirs = number(shown.value);
  if (mine != null && theirs != null && mine === theirs) return null;
  return `Property Database record: ${formatFieldValue(recordField, shown.value)} (${whoLabel(shown, now)})`;
}

/** Each ranked field's name on the record page. */
export const FIELD_LABELS: Readonly<Record<RankedField, string>> = {
  building_size: 'Building size', year_built: 'Year built', lot_size_acres: 'Lot size', land_area_sf: 'Land area',
  owner_name: 'Owner', owner_vesting: 'Vesting', last_sale_price: 'Last sale price', last_sale_date: 'Last sale date',
};

/**
 * Another source's number, in one line: "title report, Nov 1: 5,000 SF - "Building area: 5,000 square feet" - not
 * checked yet", or "OM, Sep 3: 7,800 SF (no sentence kept)". His own earlier number names him and keeps his note.
 */
export function alsoLabel(field: RankedField, v: ValueShown, now: Date): string {
  const value = formatFieldValue(field, v.value);
  const when = whenLabel(v.date, now);
  if (v.source === 'manual') {
    const who = joined(firstName(v.setByName) ?? 'Someone', v.setOn ? `on the ${v.setOn} deal` : '', when);
    return `${who}: ${value}${v.note ? ` - "${v.note}"` : ''}`;
  }
  const from = joined(sourceWords(v) || 'source not recorded', when);
  const quoted = v.sentence ? ` - "${v.sentence}"` : ' (no sentence kept)';
  return `${from}: ${value}${quoted}${v.notChecked ? ' - not checked yet' : ''}`;
}

/** The LOI's boxes a record number fills (design 4.6 rule 9). */
export type LoiNotCheckedKey = 'buildingSf' | 'acres' | 'lotSf';
const LOI_KEY_OF: Partial<Record<RankedField, LoiNotCheckedKey>> = { building_size: 'buildingSf', lot_size_acres: 'acres', land_area_sf: 'lotSf' };
const LOI_KEYS: readonly LoiNotCheckedKey[] = ['buildingSf', 'acres', 'lotSf'];
type LoiNumbers = { buildingSf?: unknown; acres?: unknown; lotSf?: unknown };
const same = (a: unknown, b: unknown): boolean => { const x = number(a); const y = number(b); return x != null && y != null && x === y; };

/** The LOI boxes whose record number came from a title report nobody has checked, from the record's field view. */
export function notCheckedLoiKeys(view: FieldValuesView | null): LoiNotCheckedKey[] {
  if (!view) return [];
  const keys = view.lines.filter((l) => l.inUse?.notChecked).map((l) => LOI_KEY_OF[l.field]).filter((k): k is LoiNotCheckedKey => !!k);
  return LOI_KEYS.filter((k) => keys.includes(k));
}

/** The marks the letter opens with: only the boxes that hold the record's own number (never one he stated in the chat). */
export function notCheckedKept(keys: LoiNotCheckedKey[], shown: LoiNumbers, record: LoiNumbers | null): LoiNotCheckedKey[] {
  if (!record) return [];
  return keys.filter((k) => same(shown[k], record[k]));
}

/** The marks still true on screen: a box he typed over is his number now. Before the boxes are read, every mark stands. */
export function stillNotChecked(
  keys: LoiNotCheckedKey[] | undefined, prefill: LoiNumbers,
  onScreen: { buildingSf: number | null; lotSizeAcres: number | null; lotSf: number | null } | null,
): LoiNotCheckedKey[] {
  const marks = keys ?? [];
  if (!onScreen) return marks;
  const boxes: Record<LoiNotCheckedKey, number | null> = { buildingSf: onScreen.buildingSf, acres: onScreen.lotSizeAcres, lotSf: onScreen.lotSf };
  return marks.filter((k) => same(prefill[k], boxes[k]));
}

/** The words under a box whose number is not checked. */
export const NOT_CHECKED_MARK = 'from the title report, not checked';

/** The lease proposal's Rentable SF box carries the mark when the record's building size is not checked - unless he stated the size. */
export function notCheckedLeaseKeys(view: FieldValuesView | null, stated: { squareFeet?: unknown }): Array<'squareFeet'> {
  if (!view || stated?.squareFeet != null) return [];
  return view.lines.some((l) => l.field === 'building_size' && l.inUse?.notChecked) ? ['squareFeet'] : [];
}

/** "This title report names 125 Main St; this record is 123 Main St - use its numbers?" */
export function askLabel(ask: RecordAskView): string {
  const kind = KIND_WORDS[ask.docKind] ?? 'document';
  const mine = ask.recordAddress ? `this record is ${ask.recordAddress}` : 'this record has no address';
  return `This ${kind} names ${ask.names}; ${mine} - use its numbers?`;
}
